'use client'

import { Grid, IconButton, Stack, Typography } from '@mui/material';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import { useLabels } from '@/utils/i18n';
import { FooterRoot, FooterContainer, CopyrightRow } from './Footer.styles';
import { FooterLink } from './FooterLink';
import { useTheme } from '@mui/material/styles';
import { RefObject } from 'react';

interface FooterProps {
  servicesRef: RefObject<HTMLHeadingElement>
  industriesRef: RefObject<HTMLHeadingElement>
  aboutRef: RefObject<HTMLHeadingElement>
}

export function Footer({ servicesRef, industriesRef, aboutRef }: FooterProps) {
  const labels = useLabels();
  const theme = useTheme();
  const maxWidth = theme.breakpoints.values.lg;

  const sectionRefs: Record<string, RefObject<HTMLHeadingElement>> = {
    services: servicesRef,
    industries: industriesRef,
    about: aboutRef,
  }

  const handleNavClick = (href: string) => {
    if (href.includes('#')) {
      const hash = href.split('#')[1]
      const ref = sectionRefs[hash]
      if (ref && ref.current) {
        const appBarHeight = 64
        const paperPadding = window.innerWidth >= 1200 ? 48 : 0
        const totalOffset = appBarHeight + paperPadding
        ref.current.style.scrollMarginTop = `${totalOffset}px`
        ref.current.scrollIntoView({ behavior: 'smooth' })
        setTimeout(() => {
          ref.current && (ref.current.style.scrollMarginTop = '')
        }, 1000)
      }
    }
  }

  return (
    <FooterRoot component="footer">
      <FooterContainer sx={{ maxWidth, mx: 'auto' }}>
        <Grid container spacing={4} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>{labels.footer.company}</Typography>
            <Stack spacing={1}>
              <FooterLink 
                href="/#about" 
                onClick={(e) => {
                  e.preventDefault()
                  handleNavClick('/#about')
                }}
              >
                {labels.footer.about}
              </FooterLink>
              <FooterLink 
                href="/#industries" 
                onClick={(e) => {
                  e.preventDefault()
                  handleNavClick('/#industries')
                }}
              >
                {labels.footer.industries}
              </FooterLink>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>{labels.footer.services}</Typography>
            <Stack spacing={1}>
              <FooterLink 
                href="/#services" 
                onClick={(e) => {
                  e.preventDefault()
                  handleNavClick('/#services')
                }}
              >
                {labels.footer.financialPlanning}
              </FooterLink>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>{labels.footer.resources}</Typography>
            <Stack spacing={1}>
              <FooterLink href="/privacy">{labels.footer.privacy}</FooterLink>
            </Stack>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle1" fontWeight={700} color="primary" gutterBottom>{labels.footer.social}</Typography>
            <Stack direction="row" spacing={1}>
              <IconButton href={labels.footer.linkedin} color="primary" aria-label="LinkedIn" sx={{'&:hover': { bgcolor: 'primary.light' }}}><LinkedInIcon /></IconButton>
              <IconButton href={labels.footer.mailto} color="primary" aria-label={labels.footer.email} sx={{'&:hover': { bgcolor: 'primary.light' }}}><EmailIcon /></IconButton>
            </Stack>
          </Grid>
        </Grid>
        <CopyrightRow>
          {labels.footer.copyright}
        </CopyrightRow>
      </FooterContainer>
    </FooterRoot>
  );
} 